// ─── pages/Archived.jsx ──────────────────────────────────────────────────────
import { useState, useEffect, useCallback } from "react";
import config from "../config";
import Swal from "sweetalert2";
import { C, LabelPill, Btn } from "../Create_plan_Trello/Shared";

// ─── Swal ─────────────────────────────────────────────────────────────────────
const swalBase = {
  background: "#1e293b", color: "#f1f5f9",
  confirmButtonColor: "#579dff", cancelButtonColor: "#334155",
  customClass: { popup: "swb-popup" },
};
const swalConfirm = async (title, text = "", confirmText = "Delete") =>
  (await Swal.fire({ ...swalBase, title, text, icon: "warning", showCancelButton: true,
    confirmButtonText: confirmText, cancelButtonColor: "#334155", confirmButtonColor: "#ef4444" })).isConfirmed;
const swalSuccess = (title) =>
  Swal.fire({ ...swalBase, icon: "success", title, timer: 1500, showConfirmButton: false });
const swalError = (text) =>
  Swal.fire({ ...swalBase, icon: "error", title: "Oops!", text });

// ─── Current user ─────────────────────────────────────────────────────────────
const getUser = () => {
  try {
    const admin = localStorage.getItem("admin");
    if (admin) { const p = JSON.parse(admin); return p.username || p.fullNames || "System"; }
    const user = localStorage.getItem("user");
    if (user) return JSON.parse(user).username || "System";
  } catch {}
  return "System";
};

// ─── Card parsers ─────────────────────────────────────────────────────────────
const fromApiCard = (card) => ({
  ...card,
  desc: card.description ?? "",
  labels: Array.isArray(card.labels)
    ? card.labels
    : (card.labels ? card.labels.split(",").map(s => s.trim()).filter(Boolean) : []),
  checklist: (() => {
    if (Array.isArray(card.checklist)) return card.checklist;
    try { return JSON.parse(card.checklist || "[]"); } catch { return []; }
  })(),
  status: card.status ?? "todo",
  attachments: card.attachments ?? [],
});

const toApiCard = (card) => ({
  id:          card.id ?? 0,
  colId:       card.colId,
  title:       card.title?.trim() || "",
  description: card.desc ?? card.description ?? "",
  labels:      Array.isArray(card.labels) ? card.labels.join(",") : (card.labels ?? ""),
  checklist:   Array.isArray(card.checklist) ? JSON.stringify(card.checklist) : (card.checklist ?? "[]"),
  archived:    card.archived ?? false,
  status:      card.status ?? "todo",
  createdBy:   card.createdBy || getUser(),
  createdAt:   card.createdAt
    ? (typeof card.createdAt === "number" ? new Date(card.createdAt).toISOString() : card.createdAt)
    : new Date().toISOString(),
});

// ─── Status labels ────────────────────────────────────────────────────────────
const STATUS_LABELS = {
  todo:       { label: "To Do",       color: "#579dff", emoji: "📋" },
  inprogress: { label: "In Progress", color: "#faa53d", emoji: "⚙️"  },
  pending:    { label: "Pending",     color: "#f87168", emoji: "⏳" },
  complete:   { label: "Complete",    color: "#4bce97", emoji: "✅" },
};

// ─── Spinner ──────────────────────────────────────────────────────────────────
function Spinner() {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, padding: "80px 0" }}>
      <div style={{
        width: 36, height: 36, borderRadius: "50%",
        border: "3px solid #1a3050",
        borderTop: "3px solid #579dff",
        animation: "arch-spin .8s linear infinite",
      }} />
      <span style={{ color: "#4a6a8a", fontSize: 13 }}>Loading archived cards…</span>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
// NOTE: No props needed from App.jsx — this fetches everything itself!
export default function Archived() {
  const [archivedCards, setArchivedCards] = useState([]);
  const [columns,       setColumns]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState("");
  const [processing,    setProcessing]    = useState(new Set());

  // ── Fetch columns + archived cards independently ───────────────────────────
const fetchData = useCallback(async () => {
  setLoading(true);
  try {
    const currentUser = getUser();

    // 1) Fetch user's columns
    const colsRes = await fetch(`${config.API_BASE_URL}/boards`);
    const allCols = colsRes.ok ? await colsRes.json() : [];
    const userCols = allCols.filter(c => c.createdBy === currentUser);
    setColumns(userCols);

    // 2) Fetch ARCHIVED cards directly — dedicated endpoint na!
    const cardsRes = await fetch(`${config.API_BASE_URL}/cards/archived`);
    if (!cardsRes.ok) throw new Error(`HTTP ${cardsRes.status}`);
    const allArchived = (await cardsRes.json()).map(fromApiCard);

    // 3) Filter — only cards na belong sa user's columns OR createdBy user
    const userColIds = new Set(userCols.map(c => c.id));
    const myArchived = allArchived.filter(c =>
      userColIds.has(c.colId) || c.createdBy === currentUser
    );

    setArchivedCards(myArchived);
  } catch (err) {
    await swalError("Failed to load archived cards.");
  } finally {
    setLoading(false);
  }
}, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getColName  = (id) => columns.find(c => c.id === id)?.title ?? "Unknown";
  const getColColor = (id) => columns.find(c => c.id === id)?.color ?? "#6b8caf";

  const setBusy = (id, busy) =>
    setProcessing(p => { const n = new Set(p); busy ? n.add(id) : n.delete(id); return n; });

  // ── Restore ────────────────────────────────────────────────────────────────
  const restore = async (card) => {
    setBusy(card.id, true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/cards/${card.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(toApiCard({ ...card, archived: false })),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setArchivedCards(p => p.filter(c => c.id !== card.id));
      await swalSuccess("Card restored! ♻️");
    } catch {
      await swalError("Failed to restore card.");
    } finally {
      setBusy(card.id, false);
    }
  };

  // ── Permanent delete ───────────────────────────────────────────────────────
  const remove = async (card) => {
    if (!await swalConfirm(`Delete "${card.title}"?`, "This cannot be undone.", "Yes, delete it")) return;
    setBusy(card.id, true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/cards/${card.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setArchivedCards(p => p.filter(c => c.id !== card.id));
    } catch {
      await swalError("Failed to delete card.");
    } finally {
      setBusy(card.id, false);
    }
  };

  // ── Filter by search ───────────────────────────────────────────────────────
  const filtered = archivedCards.filter(c =>
    !search.trim() || c.title.toLowerCase().includes(search.toLowerCase())
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @keyframes arch-spin { to { transform: rotate(360deg); } }
        .arch-row { transition: opacity .15s, transform .15s, box-shadow .15s; }
        .arch-row:hover {
          opacity: 1 !important;
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(0,0,0,.4) !important;
        }
        .swb-popup { border: 1px solid #1e3a55 !important; border-radius: 16px !important; }
      `}</style>

      <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>

        {/* ── Header ────────────────────────────────────────────────── */}
        <div style={{
          display: "flex", alignItems: "center",
          justifyContent: "space-between", marginBottom: 20,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 18 }}>🗃</span>
            <span style={{ fontSize: 15, fontWeight: 700, color: C.text ?? "#dce8f5" }}>
              Archived Cards
            </span>

            {/* Count badge */}
            {!loading && (
              <span style={{
                fontSize: 11, fontWeight: 700,
                color: "#4a6a8a",
                background: "#152438",
                padding: "2px 9px", borderRadius: 12,
                border: "1px solid #1a3050",
              }}>
                {filtered.length}
              </span>
            )}

            {/* Refresh button */}
            <button
              onClick={fetchData}
              disabled={loading}
              title="Refresh list"
              style={{
                background: "transparent",
                border: "1px solid #1a3050",
                borderRadius: 7,
                color: "#6b8caf",
                fontSize: 14,
                padding: "3px 10px",
                cursor: loading ? "default" : "pointer",
                fontFamily: "inherit",
                opacity: loading ? 0.4 : 1,
                transition: "all .15s",
              }}
            >
              ↻
            </button>
          </div>

          {/* Search */}
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="🔍  Search archived…"
            style={{
              background: C.surface ?? "#152438",
              border: "1px solid #1a3050",
              borderRadius: 8,
              padding: "8px 14px",
              fontSize: 13,
              color: C.text ?? "#dce8f5",
              outline: "none",
              fontFamily: "inherit",
              width: 220,
              transition: "border-color .15s",
            }}
            onFocus={e => e.target.style.borderColor = "#579dff"}
            onBlur={e => e.target.style.borderColor = "#1a3050"}
          />
        </div>

        {/* ── Loading ──────────────────────────────────────────────── */}
        {loading && <Spinner />}

        {/* ── Empty state ──────────────────────────────────────────── */}
        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <div style={{ fontSize: 48, marginBottom: 14 }}>🗃</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#8aaccc", marginBottom: 6 }}>
              {search ? "No results found" : "No archived cards"}
            </div>
            <div style={{ fontSize: 13, color: "#4a6a8a" }}>
              {search
                ? `No archived cards matching "${search}"`
                : "Archive a card from the board to see it here."}
            </div>
          </div>
        )}

        {/* ── Card list ─────────────────────────────────────────────── */}
        {!loading && filtered.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {filtered.map(card => {
              const busy   = processing.has(card.id);
              const st     = STATUS_LABELS[card.status] ?? STATUS_LABELS.todo;
              const colClr = getColColor(card.colId);

              return (
                <div
                  key={card.id}
                  className="arch-row"
                  style={{
                    display: "flex", alignItems: "center", gap: 14,
                    padding: "12px 16px",
                    background: C.surface ?? "#152438",
                    border: "1px solid #1a3050",
                    borderRadius: 10,
                    opacity: busy ? 0.45 : 0.88,
                    pointerEvents: busy ? "none" : "auto",
                  }}
                >
                  {/* ── Info ──────────────────────────────────────── */}
                  <div style={{ flex: 1, minWidth: 0 }}>

                    {/* Title */}
                    <div style={{
                      fontSize: 13, fontWeight: 600,
                      color: C.text ?? "#dce8f5",
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      marginBottom: 5,
                    }}>
                      {card.title}
                    </div>

                    {/* Labels */}
                    {card.labels?.length > 0 && (
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 6 }}>
                        {card.labels.map(l => <LabelPill key={l} color={l} />)}
                      </div>
                    )}

                    {/* Meta badges */}
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>

                      {/* Column */}
                      <span style={{
                        fontSize: 10, fontWeight: 700,
                        color: colClr,
                        background: `${colClr}20`,
                        padding: "2px 8px", borderRadius: 20,
                        border: `1px solid ${colClr}33`,
                      }}>
                        📂 {getColName(card.colId)}
                      </span>

                      {/* Status */}
                      <span style={{
                        fontSize: 10, fontWeight: 600,
                        color: st.color,
                        background: `${st.color}18`,
                        padding: "2px 8px", borderRadius: 20,
                        border: `1px solid ${st.color}33`,
                        display: "inline-flex", alignItems: "center", gap: 3,
                      }}>
                        {st.emoji} {st.label}
                      </span>

                      {/* Date */}
                      <span style={{ fontSize: 11, color: "#6b8caf" }}>
                        {card.createdAt ? new Date(card.createdAt).toLocaleDateString() : ""}
                      </span>

                      {/* Checklist */}
                      {card.checklist?.length > 0 && (
                        <span style={{
                          fontSize: 10, color: "#6b8caf",
                          background: "#0f1e30",
                          padding: "2px 8px", borderRadius: 20,
                        }}>
                          ✓ {card.checklist.filter(i => i.done).length}/{card.checklist.length}
                        </span>
                      )}

                      {/* Created by */}
                      {card.createdBy && (
                        <span style={{ fontSize: 10, color: "#4a6a8a", fontStyle: "italic", marginLeft: "auto" }}>
                          by {card.createdBy}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* ── Actions ───────────────────────────────────── */}
                  <div style={{ display: "flex", gap: 6, flexShrink: 0, alignItems: "center" }}>
                    {busy ? (
                      <div style={{
                        width: 20, height: 20, borderRadius: "50%",
                        border: "2px solid #1a3050",
                        borderTop: "2px solid #579dff",
                        animation: "arch-spin .7s linear infinite",
                      }} />
                    ) : (
                      <>
                        <Btn label="♻️ Restore" onClick={() => restore(card)} small />
                        <Btn label="🗑 Delete"  onClick={() => remove(card)}  danger small />
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}